import type { MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'
import { useNotesDialog } from './NotesDialogContext'

const labels: Record<DeskViewMode, string> = {
  msg: 'Notes',
  answers: 'Answers',
  submsges: 'Messages',
  attachments: 'Files',
  favs: 'Pinned',
  drafts: 'Drafts',
  trash: 'Trash',
}

const icons: Record<DeskViewMode, string> = {
  msg: '◆',
  answers: '✓',
  submsges: '▤',
  attachments: '▱',
  favs: '★',
  drafts: '◫',
  trash: '⌫',
}

export function NotesMenu({
  isEditorOpen,
  editorMode,
  settingsOpen,
  onToggleSettings,
}: {
  isEditorOpen: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
}) {
  const menu = useDeskMenu()
  const notesDialog = useNotesDialog()
  const locked = !menu.access.showUnlocked
  const saveLabel = editorMode === 'new_answer' ? 'Save Answer' : editorMode === 'new_submsg' ? 'Save Message' : 'Save'

  function navigate(event: MouseEvent<HTMLButtonElement>, mode: DeskViewMode) {
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  if (locked) return null

  return (
    <nav
      {...legacyContractDataAttrs('sidebar.root')}
      className="fixed bottom-0 left-0 top-0 z-[100000] flex w-20 flex-col border-r border-[#e0e0e0] bg-white pt-16 text-sm text-[#3c4043] md:w-64"
      aria-label="Notes controls"
    >
      <div className="flex-1 overflow-y-auto py-2">
          {isEditorOpen ? (
            <>
              <button type="button" className="mx-2 mb-1 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full bg-[#feefc3] px-5 font-medium" onClick={event => dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'notes-menu:before-save' })}>
                <span>✓</span><span className="hidden md:inline">{saveLabel}</span>
              </button>
              <button type="button" className="mx-2 mb-1 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 hover:bg-[#f1f3f4]" aria-pressed={settingsOpen} onClick={onToggleSettings}>
                <span>⚙</span><span className="hidden md:inline">{settingsOpen ? 'Content' : 'Configure'}</span>
              </button>
              <button type="button" className="mx-2 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 text-rose-700 hover:bg-rose-50" onClick={event => navigate(event, 'msg')}>
                <span>×</span><span className="hidden md:inline">Close</span>
              </button>
            </>
          ) : (
            <>
              {menu.tabs.filter(tab => tab.available && tab.hasItems && (!tab.requiresEdit || menu.access.editUnlocked) && (tab.mode !== 'favs' || menu.can.favs)).map(tab => (
                <button
                  key={tab.mode}
                  type="button"
                  className={`mx-2 mb-1 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 text-left font-medium transition-colors ${tab.isActive ? 'bg-[#feefc3] text-[#202124]' : 'hover:bg-[#f1f3f4]'}`}
                  aria-current={tab.isActive ? 'page' : undefined}
                  onClick={event => navigate(event, tab.mode)}
                >
                  <span className="w-5 flex-none text-center text-lg" aria-hidden="true">{icons[tab.mode]}</span>
                  <span className="hidden md:inline">{labels[tab.mode]}</span>
                </button>
              ))}
              <button
                type="button"
                className="mx-2 mt-3 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 text-left font-medium hover:bg-[#f1f3f4]"
                onClick={notesDialog.openMain}
              >
                <span className="w-5 flex-none text-center text-lg" aria-hidden="true">▣</span>
                <span className="hidden md:inline">Main note</span>
              </button>
              <ParentsMenu
                skin={{
                  root: 'relative mx-2',
                  trigger: 'flex h-12 w-full items-center gap-5 rounded-r-full px-5 text-left font-medium hover:bg-[#f1f3f4]',
                  triggerOpen: 'bg-[#feefc3] text-[#202124]',
                  count: 'ml-auto text-[10px]',
                  list: 'absolute left-full top-0 z-[100010] ml-1 flex min-w-56 flex-col rounded-lg border border-[#e0e0e0] bg-white p-2 text-xs shadow-lg',
                  link: 'rounded px-3 py-2 text-[#1a73e8] hover:bg-[#f1f3f4]',
                  homeLink: 'rounded px-3 py-2 text-[#5f6368] hover:bg-[#f1f3f4]',
                }}
              />
              {menu.can.reply ? (
                <button type="button" className="mx-2 mt-3 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 font-medium hover:bg-[#f1f3f4]" onClick={() => dispatchEditorAction({ action: 'open-reply' })}>
                  <span className="w-5 text-center text-xl">＋</span><span className="hidden md:inline">New note</span>
                </button>
              ) : null}
              {menu.can.createMessage ? (
                <button type="button" className="mx-2 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 font-medium hover:bg-[#f1f3f4]" onClick={() => dispatchEditorAction({ action: 'open-new-message' })}>
                  <span className="w-5 text-center text-xl">＋</span><span className="hidden md:inline">New message</span>
                </button>
              ) : null}
              <button type="button" className="mx-2 flex h-12 w-[calc(100%-1rem)] items-center gap-5 rounded-r-full px-5 font-medium hover:bg-[#f1f3f4]" onClick={() => dispatchEditorAction({ action: 'open-edit-message' })}>
                <span className="w-5 text-center">✎</span><span className="hidden md:inline">Edit desk</span>
              </button>
            </>
          )}
      </div>
      <div className="border-t border-[#e0e0e0] p-2">
          <DeskStateBadge skin={{
            root: 'hidden md:flex px-3 py-1 text-[11px]',
            possessedOpen: 'text-emerald-700', possessedPin: 'text-emerald-700', locked: 'text-[#5f6368]',
            label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline',
            form: 'mt-1 flex gap-1', input: 'w-24 rounded border px-1', button: 'text-[#1a73e8]',
          }} />
          <AuthEntry skin={{
            root: 'flex items-center justify-center gap-1 md:justify-start',
            signInButton: 'rounded-full px-3 py-2 hover:bg-[#f1f3f4]',
            identity: 'flex min-w-0 items-center gap-1',
            identityLink: 'hidden truncate text-xs text-[#1a73e8] md:block',
            signOutButton: 'rounded-full px-2 py-2 text-xs hover:bg-[#f1f3f4]',
          }} />
      </div>
    </nav>
  )
}
